import { FC, useMemo } from "react";
import { format } from "date-fns";
import { uk } from "date-fns/locale/uk";
import { useNavigate } from "react-router-dom";
// @ts-ignore
import ShakaPlayer from "shaka-player-react";
import { Divider, Image, Pill, Timer, Typography } from "theme";
import { Calendar, Chat, Location, Phone } from "theme/icons";

import { Box, Grid2 } from "@mui/material";
import { styled } from "@mui/material/styles";

import { BookingResponse } from "../../api/hooks/useBookingById/type.ts";
import { InvertButton } from "../../theme/components/Button/Button.tsx";
import LocationMap from "../LocationMap/LocationMap.tsx";

import "shaka-player/dist/controls.css";

type BookingInfoProps = {
  booking: BookingResponse;
};

const PaymentStatus: FC<{
  status: string;
}> = ({ status }) => {
  switch (status) {
    case "PaymentPending":
      return <Pill variant="warning" />;
    case "PaymentMade":
    case "PaymentCompleted":
      return <Pill variant="success" />;
    case "PaymentCanceled":
      return <Pill variant="error" />;
    default:
      return null;
  }
};

const StyledButtonWrap = styled(Box)(({ theme }) => {
  return {
    position: "fixed",
    width: "calc(100% - 20px)",
    maxWidth: 580,
    zIndex: 1,
    bottom: theme.spacing(4),
    left: "50%",
    transform: "translate(-50%, 0)",
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
  };
});

const StyledImage = styled("img")(({ theme }) => {
  return {
    height: "88px",
    width: "88px",
    objectFit: "contain",
    backgroundColor: theme.palette.common.white,
  };
});

const StyledLink = styled("a")(({ theme }) => {
  return {
    display: "flex",
    columnGap: theme.spacing(0.5),
    textDecoration: "none",
    alignItems: "center",
    color: theme.palette.text.primary,
  };
});

const BookingInfo: FC<BookingInfoProps> = ({ booking }) => {
  const navigate = useNavigate();
  const selectedTime = useMemo(() => {
    return `${format(booking.hallDetails.startDate, "EEEE, dd MMMM HH:mm", {
      locale: uk,
    })} - ${format(booking.hallDetails.endDate, "HH:mm")}`;
  }, [booking.hallDetails.endDate, booking.hallDetails.startDate]);

  return (
    <Box mb={6}>
      <Divider />

      <Box my={2.5}>
        <Typography variant="h2" textTransform="uppercase">
          Інформація про бронювання:
        </Typography>
        <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
          <Calendar />
          <Typography variant="body1">{selectedTime}</Typography>
        </Grid2>
      </Box>

      <Divider />

      <Box my={2.5}>
        <PaymentStatus status={booking.paymentStatus} />
        {booking.paymentStatus === "PaymentPending" && (
          <>
            <Box mt={1.25}>
              <Typography variant="body1">
                Має бути сплачено протягом:{" "}
                <Timer targetDate={new Date(booking.paymentLinkValidTo)} />
              </Typography>
            </Box>
            <Box mt={1.25}>
              <Typography variant="body1" weight="semiBold">
                Оплатити тут:{" "}
              </Typography>
              <a href={booking.paymentLink} target="_blank">
                <Typography variant="body1">{booking.paymentLink}</Typography>
              </a>
            </Box>
          </>
        )}
      </Box>

      <Divider />

      <Box my={2.5}>
        <Typography variant="h2" textTransform="uppercase">
          Інформація про Оренду
        </Typography>

        <Grid2 mt={2.5} width="100%" container justifyContent="space-between" alignItems="center">
          <Box>
            <Grid2 container columnGap={1.5}>
              <Image src={booking.hallDetails.imageUrl} />
              <Box>
                <Typography
                  textTransform="uppercase"
                  weight="semiBold"
                  variant="body1"
                  color="primary"
                  mb={0.5}
                >
                  {booking.hallDetails.title}
                </Typography>
                <Box>
                  <Grid2 container alignItems="center" columnGap={0.75}>
                    <Typography
                      weight="regular"
                      variant="body1"
                      color="primary"
                      textTransform="capitalize"
                    >
                      {`${format(booking.hallDetails.startDate, "HH:mm")} - ${format(booking.hallDetails.endDate, "HH:mm")}`}
                    </Typography>
                  </Grid2>
                </Box>
              </Box>
            </Grid2>
          </Box>
        </Grid2>
      </Box>

      <Divider />

      {booking.equipmentsDetails.length > 0 && (
        <>
          <Box my={2.5}>
            {booking.equipmentsDetails.map((eq) => {
              return (
                <Grid2
                  key={eq.id}
                  columnGap={1.5}
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Grid2 my={1.25} container columnGap={1.5} flexWrap="nowrap">
                      <Box>
                        <StyledImage
                          src={eq.imageUrl}
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = "/camera-image.jpg";
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body1" weight="regular" textTransform="uppercase">
                          {eq.title}
                        </Typography>

                        <Typography my={0.5} variant="body2" weight="regular">
                          Кількість: {eq.bookedCount}
                        </Typography>

                        <Typography my={0.5} variant="body2" weight="regular">
                          Час оренди:{" "}
                          {`${format(eq.startDate, "HH:mm")} - ${format(eq.endDate, "HH:mm")}`}
                        </Typography>

                        <Typography variant="body2" weight="semiBold" textTransform="uppercase">
                          {eq.pricePerUnit} {eq.isFixedPrice ? "грн" : "грн/год"}
                        </Typography>
                      </Box>
                    </Grid2>
                  </Box>
                </Grid2>
              );
            })}
          </Box>
          <Divider />
        </>
      )}

      <Box mt={2.5}>
        <Box>
          <Typography
            mb={1.5}
            variant="h2"
            color="primary"
            textTransform="uppercase"
            weight="regular"
          >
            Як нас знайти?
          </Typography>
        </Box>
        <Grid2 container flexDirection="column" alignItems="center" rowGap={1.5}>
          <Box maxWidth="300px">
            <ShakaPlayer src="https://panoramaimages.blob.core.windows.net/videos/find_us.mp4" />;
          </Box>
        </Grid2>
      </Box>

      <Divider />

      <Box my={2.5}>
        <Grid2 container flexDirection="column" rowGap={2.5}>
          <Box>
            <Typography
              mb={1}
              variant="body2"
              color="primary"
              textTransform="uppercase"
              weight="regular"
            >
              Адреса:
            </Typography>
            <StyledLink href="https://maps.app.goo.gl/WWyfAHrZYBEQS4gf6" target="_blank">
              <Location />
              <Typography
                variant="body2"
                color="primary"
                textTransform="uppercase"
                weight="regular"
                style={{
                  opacity: 0.8,
                }}
              >
                вул. Жовківська 14, м. Львів, Україна
              </Typography>
            </StyledLink>
          </Box>
          <Box>
            <Typography
              mb={1}
              variant="body2"
              color="primary"
              textTransform="uppercase"
              weight="regular"
            >
              телефон:
            </Typography>
            <StyledLink href="tel:+380638561105">
              <Phone />
              <Typography
                variant="body2"
                color="primary"
                weight="regular"
                style={{
                  opacity: 0.8,
                }}
              >
                +38(063)856-11-05
              </Typography>
            </StyledLink>
          </Box>
          <Box>
            <Typography
              mb={1}
              variant="body2"
              color="primary"
              textTransform="uppercase"
              weight="regular"
            >
              Email:
            </Typography>
            <StyledLink href="mailto:panorama.studio.lviv@gmail.com">
              <Chat />
              <Typography
                variant="body2"
                color="primary"
                weight="regular"
                style={{
                  opacity: 0.8,
                }}
              >
                panorama.studio.lviv@gmail.com
              </Typography>
            </StyledLink>
          </Box>
        </Grid2>
      </Box>

      <Divider />

      <Box mt={2.5}>
        <Grid2 container flexDirection="column" rowGap={2.5}>
          <Box>
            <Typography
              mb={1.5}
              variant="h2"
              color="primary"
              textTransform="uppercase"
              weight="regular"
            >
              Місцезнаходження
            </Typography>
            <StyledLink href="https://maps.app.goo.gl/WWyfAHrZYBEQS4gf6" target="_blank">
              <Location />
              <Typography
                variant="body2"
                color="primary"
                textTransform="uppercase"
                weight="regular"
                style={{
                  opacity: 0.8,
                }}
              >
                вул. Жовківська 14, м. Львів, Україна
              </Typography>
            </StyledLink>
            <LocationMap />
          </Box>
        </Grid2>
      </Box>

      <StyledButtonWrap>
        <InvertButton onClick={() => navigate("/")} fullWidth size="large">
          Забронювати ще &gt;
        </InvertButton>
      </StyledButtonWrap>
    </Box>
  );
};

export default BookingInfo;
